import socketService from './socketInstance'
import { ref } from 'vue'

export interface AnnouncementData {
  id: number
  title: string
  content: string
  publish_now: boolean
  publish_at: string
  force_confirm?: boolean
  agent_ids?: number[]
}

export class AnnouncementSocketService {
  private isConnected = ref(false)
  private callbacks: { [key: string]: ((data: any) => void)[] } = {}
  private scheduledTimers = new Map<number, NodeJS.Timeout>()
  private checkInterval: NodeJS.Timeout | null = null

  constructor() {
    this.initializeConnection()
    this.startPeriodicCheck()
  }

  private startPeriodicCheck() {
    // Vérifier toutes les 30 secondes s'il y a des annonces programmées à publier
    this.checkInterval = setInterval(() => {
      this.checkScheduledAnnouncements()
    }, 30000)
  }

  private async checkScheduledAnnouncements() {
    // Cette fonction peut être utilisée pour vérifier avec le backend
    // si des annonces programmées doivent être publiées

    // Émettre un événement pour demander au backend de vérifier
    // les annonces programmées qui devraient être publiées maintenant
    this.send('check_scheduled_announcements', {
      timestamp: new Date().toISOString(),
    })
  }

  // Méthode pour initialiser les annonces programmées au chargement
  async initializeScheduledAnnouncements() {

    // Demander au backend la liste des annonces programmées
    this.send('get_scheduled_announcements', {
      timestamp: new Date().toISOString(),
    })
  }

  private stopPeriodicCheck() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval)
      this.checkInterval = null
    }
  }

  private initializeConnection() {
    socketService.onMessage('connect', () => {
      this.isConnected.value = true
    })

    socketService.onMessage('disconnect', () => {
      this.isConnected.value = false
    })

    this.onAnnouncementCreated((data: AnnouncementData) => {
      this.handleNewAnnouncement(data)
    })
  }

  onAnnouncementCreated(callback: (data: AnnouncementData) => void) {
    if (!this.callbacks.announcement_created) {
      this.callbacks.announcement_created = []
      socketService.onMessage('announcement_created', (data) => {
        this.callbacks.announcement_created?.forEach((cb) => cb(data))
      })
    }
    this.callbacks.announcement_created.push(callback)
  }

  private parseLocalDateTime(dateTimeString: string): Date {
    // Format: "2026-01-20T13:12:00"
    // On veut créer une date locale, pas UTC

    const [datePart, timePart] = dateTimeString.split('T')
    const [year, month, day] = datePart.split('-').map(Number)
    const [hours, minutes, seconds = 0] = timePart.split(':').map(Number)

    // Créer la date en utilisant les paramètres locaux (pas UTC)
    const localDate = new Date(year, month - 1, day, hours, minutes, seconds)

    console.log('🕐 Parsing date locale:', {
      input: dateTimeString,
      parsed: localDate.toLocaleString(),
      timestamp: localDate.getTime(),
    })

    return localDate
  }

  private handleNewAnnouncement(data: AnnouncementData) {
    console.log('🔔 Traitement de la nouvelle annonce:', {
      id: data.id,
      title: data.title,
      publish_now: data.publish_now,
      publish_at: data.publish_at,
    })

    // TOUJOURS créer la notification immédiatement
    // Le backend se chargera de la publier au bon moment via WebSocket
    console.log('✅ Notification ajoutée immédiatement:', data.title)
    this.notifyAnnouncementUpdate('published', data)

    // Créer un minuteur frontend pour afficher un toast à l'heure de publication
    if (!data.publish_now) {
      // Parser l'heure locale correctement
      const publishTimeLocal = this.parseLocalDateTime(data.publish_at)
      const currentTime = new Date()

      console.log('🕐 Gestion horaire locale:')
      console.log('  - Heure programmée (input):', data.publish_at)
      console.log('  - Heure locale actuelle:', currentTime.toLocaleString())
      console.log('  - Heure locale cible:', publishTimeLocal.toLocaleString())
      console.log('  - Timestamp cible:', publishTimeLocal.getTime())
      console.log('  - Timestamp actuel:', currentTime.getTime())

      if (currentTime < publishTimeLocal) {
        const delayMs = publishTimeLocal.getTime() - currentTime.getTime()
        const delayMinutes = Math.round(delayMs / 60000)

        console.log(`⏰ Toast programmé dans ${delayMinutes} minute(s):`, data.title)

        // Créer un minuteur pour le toast uniquement
        const timerId = setTimeout(() => {
          console.log('🔔 HEURE ATTEINTE ! Annonce maintenant visible:', data.title)
          console.log('🔔 Heure actuelle:', new Date().toLocaleString())
          this.showNotificationToast(data.title, 'Annonce programmée maintenant visible !')
          this.scheduledTimers.delete(data.id)
        }, delayMs)

        this.scheduledTimers.set(data.id, timerId)
        console.log(`⏰ Minuteur de toast créé pour ${publishTimeLocal.toLocaleString()}`)
      } else {
        console.log('✅ Heure déjà passée, notification visible immédiatement')
        this.showNotificationToast(data.title, 'Annonce programmée visible !')
      }
    }
  }

  private schedulePublication(data: AnnouncementData) {
    // Annuler le minuteur existant si présent
    this.cancelScheduledTimer(data.id)

    // Parser l'heure locale correctement
    const publishTimeLocal = this.parseLocalDateTime(data.publish_at)
    const currentTime = new Date()
    const delay = publishTimeLocal.getTime() - currentTime.getTime()

    if (delay > 0) {
      const localTimeString = publishTimeLocal.toLocaleString()

      console.log(`⏳ Notification programmée pour: ${localTimeString}`)
      console.log(`⏳ C'est l'heure locale que vous avez choisie !`)
      console.log(`⏳ Délai d'attente: ${Math.round(delay / 60000)} minute(s)`)

      const timerId = setTimeout(() => {
        console.log('🔔 PUBLICATION AUTOMATIQUE ! Annonce programmée publiée:', data.title)
        console.log('🔔 Heure actuelle:', new Date().toLocaleString())

        // Émettre l'événement de publication pour que le frontend le reçoive
        this.sendScheduledAnnouncement(data)

        // Notifier les composants locaux
        this.notifyAnnouncementUpdate('published', data)
        this.scheduledTimers.delete(data.id)

        // Afficher une notification toast
        this.showNotificationToast(data.title, 'Annonce programmée publiée !')
      }, delay)

      this.scheduledTimers.set(data.id, timerId)
      console.log(`⏰ Minuteur créé - publication à ${localTimeString}`)
    } else {
      console.log('⚠️ Le délai est négatif, publication immédiate')
      this.notifyAnnouncementUpdate('published', data)
    }
  }

  private showNotificationToast(title: string, message: string) {
    // Créer une notification toast simple
    if (typeof window !== 'undefined' && 'CustomEvent' in window) {
      const event = new CustomEvent('showToast', {
        detail: {
          title,
          message,
          type: 'success',
          duration: 5000,
        },
      })
      window.dispatchEvent(event)
    }
  }

  private cancelScheduledTimer(announcementId: number) {
    const timerId = this.scheduledTimers.get(announcementId)
    if (timerId) {
      clearTimeout(timerId)
      this.scheduledTimers.delete(announcementId)
      console.log(`⏹️ Minuteur annulé pour l'annonce ${announcementId}`)
    }
  }

  private notifyAnnouncementUpdate(type: 'published' | 'scheduled', data: AnnouncementData) {
    const event = type === 'published' ? 'announcement_published' : 'announcement_scheduled'
    if (this.callbacks[event]) {
      this.callbacks[event].forEach((cb) => cb(data))
    }
  }

  onAnnouncementPublished(callback: (data: AnnouncementData) => void) {
    if (!this.callbacks.announcement_published) {
      this.callbacks.announcement_published = []
    }
    this.callbacks.announcement_published.push(callback)
  }

  onAnnouncementScheduled(callback: (data: AnnouncementData) => void) {
    if (!this.callbacks.announcement_scheduled) {
      this.callbacks.announcement_scheduled = []
    }
    this.callbacks.announcement_scheduled.push(callback)
  }

  send(event: string, data: any) {
    socketService.send(event, data)
  }

  // Méthode pour envoyer une annonce aux agents
  sendAnnouncement(data: AnnouncementData) {
    console.log('📤 Envoi événement announcement_created aux agents:', data)

    // Envoyer l'événement principal pour le backend
    socketService.send('announcement_created', data)

    // Envoyer également un événement spécifique pour les agents
    socketService.send('agent_announcement', {
      ...data,
      target_agents: data.agent_ids || [], // S'assurer que les agents cibles sont inclus
      timestamp: new Date().toISOString(),
    })

    console.log('📤 Événements envoyés: announcement_created + agent_announcement')
  }

  // Méthode pour envoyer une annonce programmée publiée aux agents
  sendScheduledAnnouncement(data: AnnouncementData) {
    console.log('📤 Envoi événement announcement_published aux agents:', data)

    // Envoyer l'événement principal pour le backend
    socketService.send('announcement_published', data)

    // Envoyer également un événement spécifique pour les agents
    socketService.send('agent_announcement_published', {
      ...data,
      target_agents: data.agent_ids || [], // S'assurer que les agents cibles sont inclus
      timestamp: new Date().toISOString(),
    })

  }

  // Nettoyer tous les minuteurs
  clearAllTimers() {
    this.scheduledTimers.forEach((timerId, announcementId) => {
      clearTimeout(timerId)
      console.log(`⏹️ Minuteur annulé pour l'annonce ${announcementId}`)
    })
    this.scheduledTimers.clear()

    // Arrêter la vérification périodique
    this.stopPeriodicCheck()
  }

  isSocketConnected(): boolean {
    return this.isConnected.value
  }

  off(event: string, callback?: (data: any) => void) {
    if (callback && this.callbacks[event]) {
      const index = this.callbacks[event].indexOf(callback)
      if (index > -1) {
        this.callbacks[event].splice(index, 1)
      }
    } else {
      this.callbacks[event] = []
    }
  }

  disconnect() {
    socketService.disconnect()
    this.isConnected.value = false
  }
}

// Exporter une instance singleton
export const announcementSocketService = new AnnouncementSocketService()
export default announcementSocketService
